/**
 * Bonus Selector Manager
 * 
 * Handles bonus selection logic within the deposit modal.
 * Works like mobile auth flow:
 * - First bonus is auto-selected and shown
 * - Other bonuses are hidden
 * - "Change bonus" button shows all bonuses (replaces left side on desktop, full modal on mobile)
 * 
 * @package betbeast
 */

export class BonusSelector {
    constructor(modalElement, onBonusChange, bonusesAPI = null, onLimitsChange = null) {
        this.modal = modalElement;
        this.onBonusChange = onBonusChange || (() => {});
        this.bonusesAPI = bonusesAPI;
        // Called whenever the selected bonus's deposit range may have changed (its
        // per-plan guards were (re)applied), so the controller can recompute the
        // effective amount input min/max. Bonus *selection* changes go through
        // onBonusChange instead.
        this.onLimitsChange = onLimitsChange || (() => {});
        this.selectedBonusCode = null;
        this.isShowingAllBonuses = false;
        this.isMobile = window.innerWidth < 768;
        // Eligibility refresh state: cache results per (method, amount), skip
        // unchanged keys, and ignore out-of-order responses.
        this._planCache = new Map();
        this._reqSeq = 0;
        this._currentKey = '';
        // Last amount eligibility was evaluated for; used by the min/max notice when
        // the selection changes without the amount changing.
        this._currentAmount = 0;

        if (!this.modal) {
            console.warn('Modal element not provided to BonusSelector');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize bonus selection event listeners
     */
    init() {
        // Auto-select first bonus
        this.autoSelectFirstBonus();
        
        // Bonus checkboxes
        const bonusCheckboxes = this.modal.querySelectorAll('.wallet-deposit-bonus-checkbox');
        bonusCheckboxes.forEach(checkbox => {
            checkbox.addEventListener('change', (e) => this.handleBonusSelection(e.target));
        });
        
        // Make entire bonus items clickable
        const bonusItems = this.modal.querySelectorAll('.wallet-deposit-bonus-item');
        bonusItems.forEach(item => {
            item.addEventListener('click', (e) => {
                // Don't trigger if clicking on the checkbox itself or label
                if (e.target.classList.contains('wallet-deposit-bonus-checkbox') || 
                    e.target.tagName === 'LABEL') {
                    return;
                }
                
                const checkbox = item.querySelector('.wallet-deposit-bonus-checkbox');
                if (checkbox) {
                    // If already checked, treat as confirmation and go back
                    if (checkbox.checked) {
                        this.hideAllBonuses();
                    } else {
                        // Otherwise, check it
                        checkbox.checked = true;
                        checkbox.dispatchEvent(new Event('change'));
                    }
                }
            });
        });
        
        // Make "no bonus" box clickable
        const noBonusBox = this.modal.querySelector('.wallet-deposit-no-bonus-box');
        if (noBonusBox) {
            noBonusBox.addEventListener('click', (e) => {
                // Don't trigger if clicking on the checkbox itself or label
                if (e.target.classList.contains('wallet-deposit-no-bonus') || 
                    e.target.tagName === 'LABEL') {
                    return;
                }
                
                const checkbox = noBonusBox.querySelector('.wallet-deposit-no-bonus');
                if (checkbox) {
                    // If already checked, treat as confirmation and go back
                    if (checkbox.checked) {
                        this.hideAllBonuses();
                    } else {
                        // Otherwise, check it
                        checkbox.checked = true;
                        checkbox.dispatchEvent(new Event('change'));
                    }
                }
            });
        }
        
        // No bonus checkbox
        const noBonusCheckbox = this.modal.querySelector('.wallet-deposit-no-bonus');
        if (noBonusCheckbox) {
            noBonusCheckbox.addEventListener('change', (e) => this.handleNoBonusSelection(e.target));
        }
        
        // Change bonus button (show all bonuses)
        const changeBonusBtn = this.modal.querySelector('[data-show-all-bonuses]');
        if (changeBonusBtn) {
            changeBonusBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.showAllBonuses();
            });
        }
        
        // Selected bonus card - make entire card clickable
        const selectedBonusCard = this.modal.querySelector('.wallet-deposit-selected-bonus');
        if (selectedBonusCard) {
            selectedBonusCard.addEventListener('click', () => this.showAllBonuses());
        }
        
        // Back button (when viewing all bonuses)
        const backBtn = this.modal.querySelector('[data-hide-all-bonuses]');
        if (backBtn) {
            backBtn.addEventListener('click', () => this.hideAllBonuses());
        }
        
        // Mobile bonus preview button (new style - works on both mobile and desktop)
        const mobilePreviewBtn = this.modal.querySelector('[data-mobile-bonus-toggle]');
        if (mobilePreviewBtn) {
            mobilePreviewBtn.addEventListener('click', () => this.showAllBonuses());
        }
        
        // Mobile bonus preview content (entire clickable area)
        const mobilePreviewContent = this.modal.querySelector('.wallet-deposit-mobile-preview-content');
        if (mobilePreviewContent) {
            mobilePreviewContent.addEventListener('click', () => this.showAllBonuses());
        }
        
        // The deposit amount + payment method drive bonus eligibility, so the
        // controller calls refresh(method, amount) on those changes — see
        // DepositFlowController.refreshBonuses().

        // Handle window resize
        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth < 768;
        });
    }

    /**
     * Re-evaluate eligible bonus plans for the current method + amount and apply
     * the server-side eligibility (bonus amount, min/max guards, playable flag).
     *
     * Stable + low-traffic: results are cached per (method, amount); an unchanged
     * key is a no-op, a previously-seen key is served from cache (no request), and
     * out-of-order responses are dropped. Called on blur/change/quick-amount/method
     * change (not per keystroke) by DepositFlowController.refreshBonuses().
     *
     * @param {string} paymentMethod API method code (e.g. APCOPAY_V2_CARD)
     * @param {number} amount        Deposit amount (raw display value)
     */
    refresh(paymentMethod, amount) {
        const amt = parseFloat(amount) || 0;
        this._currentAmount = amt;

        // The endpoint needs a method + positive amount to evaluate eligibility;
        // until then keep the cards in a neutral (un-guarded) state.
        if (!this.bonusesAPI || !paymentMethod || amt <= 0) {
            this._currentKey = '';
            this.resetEligibility();
            return;
        }

        const key = `${paymentMethod}|${amt}`;

        // Already showing this exact combination — nothing to fetch or re-render.
        if (key === this._currentKey) {
            return;
        }
        this._currentKey = key;

        // Served from cache — no network call (covers returning to a prior value).
        if (this._planCache.has(key)) {
            this.applyEligibility(this._planCache.get(key));
            return;
        }

        // Fetch; tag the request so only the latest one is allowed to apply.
        const seq = ++this._reqSeq;
        this.bonusesAPI.fetchEligibleBonusPlans(paymentMethod, amt).then(plans => {
            this._planCache.set(key, plans);
            // Drop stale responses (a newer request started, or the inputs moved on).
            if (seq !== this._reqSeq || key !== this._currentKey) {
                return;
            }
            this.applyEligibility(plans);
        });
    }

    /**
     * Seed the server-preloaded eligibility (auto-selected method at its minimum
     * deposit) so the first paint already shows the correct list — no network
     * round-trip and no "all cards then narrow" flash. Caches it under the same key
     * the front-end derives for the empty-amount state, so the first user-driven
     * refresh dedupes instead of re-fetching.
     *
     * @param {string} method API method code the plans were fetched for.
     * @param {number} amount Amount they were fetched for (method min, display ccy).
     * @param {Array}  plans  Normalized plans (same shape as the AJAX response).
     * @returns {boolean} True when initial plans were applied.
     */
    applyInitialPlans(method, amount, plans) {
        const amt = parseFloat(amount) || 0;
        if (!method || amt <= 0 || !Array.isArray(plans)) {
            return false;
        }
        const key = `${method}|${amt}`;
        this._planCache.set(key, plans);
        this._currentKey = key;
        this._currentAmount = amt;
        this.applyEligibility(plans);
        return true;
    }

    /**
     * Neutral state used before an amount is entered: show all cards, clear any
     * stale guard data and zero the bonus amount.
     */
    resetEligibility() {
        const items = this.modal.querySelectorAll('.wallet-deposit-bonus-item');
        items.forEach(item => {
            const checkbox = item.querySelector('.wallet-deposit-bonus-checkbox');
            if (!checkbox) return;
            this._setDisplay(item, '');
            item.classList.remove('is-ineligible');
            if (checkbox.disabled) checkbox.disabled = false;
            this._setData(checkbox, 'bonusAmount', 0);
            if (!checkbox.dataset.bonusPlanId) {
                checkbox.dataset.bonusPlanId = checkbox.value;
            }
            delete checkbox.dataset.playable;
        });
        this._toggleNoBonusesMessage(false);
        this.applyAmountLock();
        this.updateBonusAmountDisplay();
        this.updateBonusNotice();
        this.onLimitsChange();
    }

    /**
     * Attach the API's per-amount guard data to the rendered cards.
     *
     * IMPORTANT: this pass never changes WHICH bonuses are shown — the displayed set
     * is owned entirely by the server-side per-party gate (Layer 1). Here we only
     * record each card's min/max deposit guards, server bonus amount and `playable`
     * flag for the entered amount (Layer 2), so the "you'll receive X" line and the
     * min/max notice can react. No card is hidden, disabled or de-selected.
     *
     * Idempotent: dataset values are only written where they actually change.
     *
     * @param {Array} plans Normalized plans from DepositBonusesAPI.
     */
    applyEligibility(plans) {
        const byId = new Map();
        (Array.isArray(plans) ? plans : []).forEach(plan => {
            if (plan && plan.bonus_plan_id != null) {
                byId.set(String(plan.bonus_plan_id), plan);
            }
        });

        const items = this.modal.querySelectorAll('.wallet-deposit-bonus-item');
        items.forEach(item => {
            const checkbox = item.querySelector('.wallet-deposit-bonus-checkbox');
            if (!checkbox) return;

            const id = item.getAttribute('data-bonus-id') || checkbox.value;
            const plan = byId.get(String(id));

            if (plan) {
                this._setData(checkbox, 'bonusPlanId', plan.bonus_plan_id);
                this._setData(checkbox, 'minDepositAmount', plan.min_deposit_amount || 0);
                this._setData(checkbox, 'maxDepositAmount', plan.max_deposit_amount || 0);
                this._setData(checkbox, 'bonusAmount', plan.bonus_amount || 0);
                this._setData(checkbox, 'bonusCurrency', plan.currency || '');
                this._setData(checkbox, 'playable', plan.playable ? '1' : '0');
            } else {
                // Not returned for this amount: keep the card visible (Layer 1 owns the
                // set) and keep any previously-known min/max so the notice can still
                // cite a figure; just mark it unplayable and zero the bonus amount.
                this._setData(checkbox, 'playable', '0');
                this._setData(checkbox, 'bonusAmount', 0);
            }

            // Always visible + enabled — Layer 2 must not alter the displayed set.
            if (checkbox.disabled) checkbox.disabled = false;
            this._setDisplay(item, '');
            item.classList.remove('is-ineligible');
        });

        this.applyAmountLock();
        this.updateBonusAmountDisplay();
        this.updateBonusNotice();
        this.onLimitsChange();
    }

    /**
     * Write a dataset value only when it actually changes (avoids needless churn).
     */
    _setData(el, key, value) {
        const next = String(value);
        if (el.dataset[key] !== next) {
            el.dataset[key] = next;
        }
    }

    /**
     * Set an element's inline display only when it changes.
     */
    _setDisplay(el, value) {
        if (el.style.display !== value) {
            el.style.display = value;
        }
    }

    /**
     * Show/hide the stable "no bonuses available for this amount" message.
     */
    _toggleNoBonusesMessage(show) {
        const msg = this.modal.querySelector('.wallet-deposit-bonuses-empty');
        if (!msg) return;
        this._setDisplay(msg, show ? '' : 'none');
    }

    /**
     * Update the "You'll receive X extra in bonus" text from the server-computed
     * bonus amount carried on the selected card (set by applyEligibility()).
     */
    updateBonusAmountDisplay() {
        const bonusInfoEl   = this.modal.querySelector('.wallet-deposit-bonus-info');
        if (!bonusInfoEl) return;

        const promptEl      = bonusInfoEl.querySelector('.wallet-deposit-bonus-text--prompt');
        const earnedEl      = bonusInfoEl.querySelector('.wallet-deposit-bonus-text--earned');
        const bonusAmountEl = bonusInfoEl.querySelector('.wallet-deposit-bonus-amount');

        const checked = this.modal.querySelector('.wallet-deposit-bonus-checkbox:checked');

        // No bonus selected → hide the whole row.
        if (!checked) {
            this._setDisplay(bonusInfoEl, 'none');
            return;
        }

        // The seeded default amount (smallest quick-amount) is not something the user
        // chose, so read the real input value — the calculated figure is only shown
        // once the player has actually entered an amount. A fixed-amount bonus locks
        // the input to its exact value (applyAmountLock), so it counts as entered.
        const amountInput   = this.modal.querySelector('.wallet-deposit-amount-input');
        const enteredAmount = amountInput ? parseFloat(amountInput.value) || 0 : 0;

        // Before an amount is entered: show a neutral prompt instead of a default number.
        if (enteredAmount <= 0) {
            this._setDisplay(bonusInfoEl, '');
            if (promptEl) this._setDisplay(promptEl, '');
            if (earnedEl) this._setDisplay(earnedEl, 'none');
            return;
        }

        const serverBonus = parseFloat(checked.dataset.bonusAmount || 0);
        // When the selected bonus isn't playable for the entered amount we show the
        // min/max notice instead, so don't also promise a bonus it won't earn.
        const playable = checked.dataset.playable !== '0';

        // Amount entered but the bonus yields nothing / isn't playable → hide the row
        // (the min/max notice explains why).
        if (!serverBonus || !playable) {
            this._setDisplay(bonusInfoEl, 'none');
            return;
        }

        // Amount entered + playable → show the calculated bonus.
        this._setDisplay(bonusInfoEl, '');
        if (promptEl) this._setDisplay(promptEl, 'none');
        if (earnedEl) this._setDisplay(earnedEl, '');

        if (bonusAmountEl) {
            // bonus_amount comes from the API computed against the amount we sent, so
            // label it with the plan's own currency (not the modal display currency).
            const currencyCode = checked.dataset.bonusCurrency || this.modal.dataset.currencyCode || '';
            const text = serverBonus.toFixed(2) + (currencyCode ? ' ' + currencyCode : '');
            if (bonusAmountEl.textContent !== text) {
                bonusAmountEl.textContent = text;
            }
        }
    }

    /**
     * Show/hide the min/max notice for the currently selected bonus.
     *
     * Layer 2 doesn't hide out-of-range bonuses anymore — instead, when the entered
     * amount falls below the selected bonus's minimum or above its maximum (the guards
     * carried on the card by applyEligibility()), we tell the player so they can adjust
     * the amount or pick another bonus. Compared in the raw entered amount, mirroring
     * the submit-time guard in PaymentHandler.
     */
    updateBonusNotice() {
        const noticeEl = this.modal.querySelector('.wallet-deposit-bonus-notice');
        if (!noticeEl) return;

        const checked = this.modal.querySelector('.wallet-deposit-bonus-checkbox:checked');
        if (!checked) {
            this._setNotice(noticeEl, '');
            return;
        }

        const min = parseFloat(checked.dataset.minDepositAmount || 0);
        const max = parseFloat(checked.dataset.maxDepositAmount || 0);
        const ccy = checked.dataset.bonusCurrency || this.modal.dataset.currencyCode || '';
        const suffix = ccy ? ' ' + ccy : '';

        // Fixed-amount bonus (min == max): the deposit must equal this exact value, so
        // the amount is locked to it (see applyAmountLock) and we say so regardless of
        // what's currently entered. Takes priority over the range messages below.
        if (min > 0 && min === max) {
            this._setNotice(noticeEl, `To use this bonus you must deposit exactly ${min}${suffix}.`);
            return;
        }

        const amount = parseFloat(this._currentAmount) || 0;

        // No amount yet, or eligibility not evaluated → no notice.
        if (amount <= 0 || checked.dataset.playable === undefined) {
            this._setNotice(noticeEl, '');
            return;
        }

        let msg = '';
        if (min && amount < min) {
            msg = `The minimum deposit for this bonus is ${min}${suffix}.`;
        } else if (max && amount > max) {
            msg = `The maximum deposit for this bonus is ${max}${suffix}.`;
        } else if (checked.dataset.playable === '0') {
            msg = "This bonus isn't available for the selected amount.";
        }

        this._setNotice(noticeEl, msg);
    }

    /**
     * Set the notice text + visibility, only touching the DOM when it changes.
     */
    _setNotice(el, msg) {
        if (msg) {
            if (el.textContent !== msg) el.textContent = msg;
            this._setDisplay(el, '');
        } else {
            this._setDisplay(el, 'none');
        }
    }

    /**
     * Exact required deposit for a fixed-amount bonus (min == max), else null.
     *
     * Reads the per-plan min/max guards attached by applyEligibility(). These are
     * static per plan and seeded on first paint, so this is reliable even before the
     * player enters an amount.
     *
     * @param {HTMLInputElement|null} checkbox Selected bonus checkbox.
     * @returns {number|null} The fixed amount, or null when the bonus isn't fixed.
     */
    _getFixedAmount(checkbox) {
        if (!checkbox) return null;
        const min = parseFloat(checkbox.dataset.minDepositAmount || 0);
        const max = parseFloat(checkbox.dataset.maxDepositAmount || 0);
        return (min > 0 && min === max) ? min : null;
    }

    /**
     * Deposit min/max guards for the currently selected bonus, as set by
     * applyEligibility(). A value of 0 means "no constraint" (nothing selected, the
     * "no bonus" option, or a plan without that bound) so the controller leaves the
     * corresponding PSP limit untouched.
     *
     * @returns {{min: number, max: number}}
     */
    getSelectedBonusLimits() {
        const checked = this.modal.querySelector('.wallet-deposit-bonus-checkbox:checked');
        if (!checked) {
            return { min: 0, max: 0 };
        }
        return {
            min: parseFloat(checked.dataset.minDepositAmount || 0) || 0,
            max: parseFloat(checked.dataset.maxDepositAmount || 0) || 0
        };
    }

    /**
     * When the selected bonus requires an exact deposit (min == max), force the amount
     * input to that value and lock it (plus the quick-amount buttons) so the player
     * can't drift off the only amount this bonus accepts. Any other selection unlocks.
     *
     * Idempotent: the value is only re-set (and events re-dispatched) when it actually
     * differs, so the refresh() this triggers converges instead of looping.
     */
    applyAmountLock() {
        const amountInput = this.modal.querySelector('.wallet-deposit-amount-input');
        if (!amountInput) return;

        const checked      = this.modal.querySelector('.wallet-deposit-bonus-checkbox:checked');
        const fixed        = this._getFixedAmount(checked);
        const quickAmounts = this.modal.querySelectorAll('.wallet-deposit-quick-amount');

        if (fixed !== null) {
            const value = String(fixed);
            if (amountInput.value !== value) {
                amountInput.value = value;
                // Mirror QuickAmountSelector: clear stale states + re-evaluate eligibility
                // (blur is what DepositFlowController listens on to refresh bonuses).
                amountInput.dispatchEvent(new Event('input', { bubbles: true }));
                amountInput.dispatchEvent(new Event('blur', { bubbles: true }));
            }
            amountInput.readOnly = true;
            amountInput.classList.add('is-locked');
            quickAmounts.forEach(btn => {
                btn.disabled = true;
                btn.classList.remove('is-active');
            });
        } else if (amountInput.readOnly) {
            amountInput.readOnly = false;
            amountInput.classList.remove('is-locked');
            quickAmounts.forEach(btn => { btn.disabled = false; });
        }
    }

    /**
     * Auto-select first available bonus
     */
    autoSelectFirstBonus() {
        const firstBonusCheckbox = this.modal.querySelector('.wallet-deposit-bonus-checkbox');
        if (firstBonusCheckbox) {
            firstBonusCheckbox.checked = true;
            this.selectedBonusCode = firstBonusCheckbox.value;
            this.onBonusChange(this.selectedBonusCode);
            this.updateSelectedBonusDisplay();
            this.updateBonusAmountDisplay();
            this.updateBonusNotice();
            this.applyAmountLock();
        }
    }

    /**
     * Handle bonus checkbox selection
     */
    handleBonusSelection(checkbox) {
        if (!checkbox.checked) {
            this.selectedBonusCode = null;
            this.onBonusChange(null);
            this.updateBonusNotice();
            this.applyAmountLock();
            return;
        }
        
        // Uncheck all other bonus checkboxes
        const allBonusCheckboxes = this.modal.querySelectorAll('.wallet-deposit-bonus-checkbox');
        allBonusCheckboxes.forEach(cb => {
            if (cb !== checkbox) {
                cb.checked = false;
            }
        });
        
        // Uncheck "no bonus" checkbox
        const noBonusCheckbox = this.modal.querySelector('.wallet-deposit-no-bonus');
        if (noBonusCheckbox) {
            noBonusCheckbox.checked = false;
        }
        
        this.selectedBonusCode = checkbox.value;
        this.onBonusChange(this.selectedBonusCode);
        this.updateSelectedBonusDisplay();
        this.updateBonusAmountDisplay();
        this.updateBonusNotice();
        this.applyAmountLock();

        // Hide all bonuses after selection
        if (this.isShowingAllBonuses) {
            this.hideAllBonuses();
        }
    }
    
    /**
     * Handle "no bonus" checkbox selection
     */
    handleNoBonusSelection(checkbox) {
        if (checkbox.checked) {
            // Uncheck all bonus checkboxes
            const allBonusCheckboxes = this.modal.querySelectorAll('.wallet-deposit-bonus-checkbox');
            allBonusCheckboxes.forEach(cb => {
                cb.checked = false;
            });
            
            this.selectedBonusCode = null;
            this.onBonusChange(null);
            this.updateSelectedBonusDisplayForNoBonus();
            this.updateBonusAmountDisplay();
            this.updateBonusNotice();
            this.applyAmountLock();

            // Hide all bonuses after selection
            if (this.isShowingAllBonuses) {
                this.hideAllBonuses();
            }
        }
    }
    
    /**
     * Update the selected bonus display for "no bonus" option
     */
    updateSelectedBonusDisplayForNoBonus() {
        const selectedBonusCard = this.modal.querySelector('.wallet-deposit-selected-bonus');
        if (!selectedBonusCard) return;
        
        const categoryEl = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-category');
        const titleEl = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-title');
        const imagesContainer = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-images');
        
        // Update text
        if (categoryEl) categoryEl.textContent = '';
        if (titleEl) titleEl.textContent = "I don't want a bonus";
        
        // Clear images
        if (imagesContainer) {
            imagesContainer.innerHTML = '';
        }
        
        // Clear bonus attributes
        selectedBonusCard.removeAttribute('data-bonus-code');
        selectedBonusCard.removeAttribute('data-bonus-id');
    }
    
    /**
     * Update the selected bonus display (show only selected bonus)
     */
    updateSelectedBonusDisplay() {
        const bonusList = this.modal.querySelector('.wallet-deposit-bonuses-list');
        if (!bonusList) return;
        
        const allBonusItems = bonusList.querySelectorAll('.wallet-deposit-bonus-item');
        let selectedBonusInfo = null;
        let selectedBonusItem = null;
        
        allBonusItems.forEach(item => {
            const checkbox = item.querySelector('.wallet-deposit-bonus-checkbox, .wallet-deposit-no-bonus');
            if (checkbox && checkbox.checked) {
                item.classList.add('is-selected');
                item.classList.remove('is-hidden');
                
                // Store reference to selected item
                selectedBonusItem = item;
                
                // Get bonus info for preview card
                const bonusCategory = item.querySelector('.wallet-deposit-bonus-category');
                const bonusHeaderText = item.querySelector('.wallet-deposit-bonus-header-text');
                selectedBonusInfo = {
                    category: bonusCategory ? bonusCategory.textContent : '',
                    title: bonusHeaderText ? bonusHeaderText.textContent : '',
                    code: checkbox.value
                };
            } else {
                item.classList.remove('is-selected');
                item.classList.add('is-hidden');
            }
        });
        
        // Update the selected bonus preview card
        const selectedBonusCard = this.modal.querySelector('.wallet-deposit-selected-bonus');
        if (selectedBonusCard && selectedBonusInfo && selectedBonusItem) {
            const categoryEl = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-category');
            const titleEl = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-title');
            
            if (categoryEl) categoryEl.textContent = selectedBonusInfo.category;
            if (titleEl) titleEl.textContent = selectedBonusInfo.title;
            
            selectedBonusCard.setAttribute('data-bonus-code', selectedBonusInfo.code);
            selectedBonusCard.setAttribute('data-bonus-id', selectedBonusInfo.code);
            
            // Update images - clone from selected bonus item
            const imagesContainer = selectedBonusCard.querySelector('.wallet-deposit-selected-bonus-images');
            const sourceImages = selectedBonusItem.querySelector('.wallet-deposit-bonus-images');
            
            if (imagesContainer && sourceImages) {
                // Clear existing images
                imagesContainer.innerHTML = '';
                
                // Clone each image
                const backgroundImg = sourceImages.querySelector('.wallet-deposit-bonus-image--background');
                const effectImg = sourceImages.querySelector('.wallet-deposit-bonus-image--effect');
                const foregroundImg = sourceImages.querySelector('.wallet-deposit-bonus-image--foreground');
                
                if (backgroundImg) {
                    const clonedBg = backgroundImg.cloneNode(true);
                    clonedBg.className = 'wallet-deposit-selected-bonus-image wallet-deposit-selected-bonus-image--background';
                    imagesContainer.appendChild(clonedBg);
                }
                
                if (effectImg) {
                    const clonedEffect = effectImg.cloneNode(true);
                    clonedEffect.className = 'wallet-deposit-selected-bonus-image wallet-deposit-selected-bonus-image--effect';
                    imagesContainer.appendChild(clonedEffect);
                }
                
                if (foregroundImg) {
                    const clonedFg = foregroundImg.cloneNode(true);
                    clonedFg.className = 'wallet-deposit-selected-bonus-image wallet-deposit-selected-bonus-image--foreground';
                    imagesContainer.appendChild(clonedFg);
                }
            }
        }
    }
    
    /**
     * Show all bonuses (for selection)
     */
    showAllBonuses() {
        this.isShowingAllBonuses = true;
        
        const selectedBonusWrapper = this.modal.querySelector('.wallet-deposit-selected-bonus-wrapper');
        const allBonusesWrapper = this.modal.querySelector('.wallet-deposit-all-bonuses-wrapper');
        const paymentMethodsWrapper = this.modal.querySelector('.wallet-deposit-payment-methods-wrapper');
        const formWrapper = this.modal.querySelector('.wallet-deposit-form-wrapper');
        const modalHeader = this.modal.querySelector('.wallet-deposit-modal__header');
        
        // Hide selected bonus preview
        if (selectedBonusWrapper) {
            selectedBonusWrapper.style.display = 'none';
        }
        
        // Show all bonuses list
        if (allBonusesWrapper) {
            allBonusesWrapper.style.display = 'block';
        }
        
        // Hide payment methods on desktop, show all bonuses
        if (paymentMethodsWrapper) {
            paymentMethodsWrapper.style.display = 'none';
        }
        
        // Hide entire form on mobile
        if (formWrapper && this.isMobile) {
            formWrapper.style.display = 'none';
        }
        
        // Hide modal header on mobile
        if (modalHeader && this.isMobile) {
            modalHeader.style.display = 'none';
        }
        
        // Show all bonus items in the list
        const allBonusItems = this.modal.querySelectorAll('.wallet-deposit-bonus-item');
        allBonusItems.forEach(item => {
            item.classList.remove('is-hidden');
            item.classList.remove('is-selected');
        });
    }
    
    /**
     * Hide all bonuses (show only selected)
     */
    hideAllBonuses() {
        this.isShowingAllBonuses = false;
        
        const selectedBonusWrapper = this.modal.querySelector('.wallet-deposit-selected-bonus-wrapper');
        const allBonusesWrapper = this.modal.querySelector('.wallet-deposit-all-bonuses-wrapper');
        const paymentMethodsWrapper = this.modal.querySelector('.wallet-deposit-payment-methods-wrapper');
        const formWrapper = this.modal.querySelector('.wallet-deposit-form-wrapper');
        const modalHeader = this.modal.querySelector('.wallet-deposit-modal__header');
        
        // Show selected bonus preview
        if (selectedBonusWrapper) {
            selectedBonusWrapper.style.display = 'block';
        }
        
        // Hide all bonuses list
        if (allBonusesWrapper) {
            allBonusesWrapper.style.display = 'none';
        }
        
        // Show payment methods again
        if (paymentMethodsWrapper) {
            paymentMethodsWrapper.style.display = 'block';
        }
        
        // Show form again on mobile
        if (formWrapper && this.isMobile) {
            formWrapper.style.display = 'flex';
        }
        
        // Show modal header again on mobile
        if (modalHeader && this.isMobile) {
            modalHeader.style.display = 'flex';
        }
        
        // Update selected bonus display
        this.updateSelectedBonusDisplay();
    }
    
    /**
     * Get currently selected bonus code
     */
    getSelectedBonusCode() {
        return this.selectedBonusCode;
    }
    
    /**
     * Set bonus code programmatically (e.g., from URL parameter)
     */
    setBonusCode(bonusCode) {
        // Try the code as given, then again with a leading `UserBonus:` removed.
        //
        // The bell sends the player here with `?bonus_code=UserBonus:{id}`
        // (notifications-button.js:141), while the checkboxes carry the bare id —
        // `value="{{ bonus.bonus_id }}"` (account-wallet-deposit.twig:100). The two numbers are
        // the same; only the prefix differed, so the lookup never matched and every arrival
        // fell through to autoSelectFirstBonus() below. That is the whole reason a bonus chosen
        // in the bell came out as "the first one" on the Apcopay deposit screen, every time.
        //
        // Matching on both forms rather than just stripping: any link already out there keeps
        // working, and if the markup ever switches to the prefixed value the lookup survives
        // that too. The prefix is not needed anywhere else — the payment request reads the id
        // straight off the checked checkbox (payment-handler.js:109-111).
        const raw = typeof bonusCode === 'string' ? bonusCode.trim() : '';
        const stripped = raw.replace(/^UserBonus:/i, '');
        const candidates = stripped === raw ? [raw] : [raw, stripped];

        let bonusCheckbox = null;
        for (const value of candidates) {
            if (!value) continue;
            bonusCheckbox = this.modal.querySelector(`.wallet-deposit-bonus-checkbox[value="${value}"]`);
            if (bonusCheckbox) break;
        }

        if (bonusCheckbox) {
            bonusCheckbox.checked = true;
            this.handleBonusSelection(bonusCheckbox);
        } else {
            // If bonus code not found, keep first bonus selected
            this.autoSelectFirstBonus();
        }
    }
    
    /**
     * Reset to initial state (first bonus selected, others hidden)
     */
    reset() {
        this.hideAllBonuses();
        this.autoSelectFirstBonus();
    }
}
